import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import API from "@/lib/api";
import { getErrorMessage } from "@/lib/errorHandler";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useCampusContext } from "@/contexts/CampusContext";
import useViewMode from "@/hooks/useViewMode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { MapPin, Plus, Pencil, Trash2, Users, Search, LayoutGrid, List, RotateCcw, AlertTriangle, Loader2, Mail } from "lucide-react";

export default function Campuses() {
  const { user, isSuperadmin } = useAuth();
  const { selectedOrg } = useOrganizationContext();
  const { reloadCampuses } = useCampusContext();
  const queryClient = useQueryClient();

  const [showDialog, setShowDialog] = useState(false);
  const [editingCampus, setEditingCampus] = useState(null);
  const [form, setForm] = useState({ name: "", slug: "", address: "" });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [hardDeleteConfirm, setHardDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useViewMode("campuses"); // "card" | "list"
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "active" | "inactive"

  const [adminPopoverCampus, setAdminPopoverCampus] = useState(null);

  // Build query params
  const queryParams = {};
  if (isSuperadmin && selectedOrg?.id) queryParams.organization_id = selectedOrg.id;

  const { data: campuses = [], isLoading } = useQuery({
    queryKey: ["campuses", queryParams],
    queryFn: async () => {
      const res = await API.get("/campuses", { params: queryParams });
      return res.data;
    },
    enabled: !isSuperadmin || !!selectedOrg?.id,
  });

  // Fetch admins for the currently open popover campus
  const { data: campusAdmins = [], isFetching: adminsFetching } = useQuery({
    queryKey: ["campus-admins", adminPopoverCampus?.id],
    queryFn: async () => {
      const params = { campus_id: adminPopoverCampus.id };
      if (isSuperadmin && selectedOrg?.id) params.organization_id = selectedOrg.id;
      const res = await API.get("/admin-management/admins", { params });
      return res.data.data ?? [];
    },
    enabled: !!adminPopoverCampus?.id,
    staleTime: 30_000,
  });

  const filteredCampuses = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return campuses.filter((c) => {
      if (statusFilter === "active" && !c.is_active) return false;
      if (statusFilter === "inactive" && c.is_active) return false;
      if (!q) return true;
      return (
        c.name?.toLowerCase().includes(q) ||
        c.slug?.toLowerCase().includes(q) ||
        c.address?.toLowerCase().includes(q)
      );
    });
  }, [campuses, searchQuery, statusFilter]);

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const payload = { ...data };
      if (isSuperadmin && selectedOrg?.id) payload.organization_id = selectedOrg.id;
      await API.post("/campuses", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["campuses"]);
      reloadCampuses();
      toast.success("Campus created successfully.");
      closeDialog();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to create campus."));
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      await API.put(`/campuses/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["campuses"]);
      reloadCampuses();
      toast.success("Campus updated successfully.");
      closeDialog();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to update campus."));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async (id) => {
      await API.delete(`/campuses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["campuses"]);
      reloadCampuses();
      toast.success("Campus deactivated.");
      setDeleteConfirm(null);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to deactivate campus."));
    },
  });

  const activateMutation = useMutation({
    mutationFn: async (id) => {
      await API.put(`/campuses/${id}`, { is_active: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["campuses"]);
      reloadCampuses();
      toast.success("Campus activated.");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to activate campus."));
    },
  });

  const hardDeleteMutation = useMutation({
    mutationFn: async (id) => {
      await API.delete(`/campuses/${id}`, { params: { hard: true } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["campuses"]);
      reloadCampuses();
      toast.success("Campus permanently deleted.");
      setHardDeleteConfirm(null);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to delete campus."));
    },
  });

  const openCreate = () => {
    setEditingCampus(null);
    setForm({ name: "", slug: "", address: "" });
    setShowDialog(true);
  };

  const openEdit = (campus) => {
    setEditingCampus(campus);
    setForm({ name: campus.name, slug: campus.slug, address: campus.address || "" });
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditingCampus(null);
    setForm({ name: "", slug: "", address: "" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim()) {
      toast.error("Name and slug are required.");
      return;
    }
    if (editingCampus) {
      updateMutation.mutate({ id: editingCampus.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  // Auto-generate slug from name
  const handleNameChange = (value) => {
    setForm(prev => ({
      ...prev,
      name: value,
      slug: !editingCampus ? value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") : prev.slug,
    }));
  };

  if (isSuperadmin && !selectedOrg) {
    return (
      <div className="text-center py-16 text-slate-500">
        Please select an organization to manage campuses.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Campuses</h1>
          <p className="text-sm text-slate-500 mt-1">Manage campus locations within your organization.</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" /> Add Campus
        </Button>
      </div>

      {/* Search + View Toggle toolbar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, slug or address…"
            className="pl-9"
          />
        </div>

        {/* Status filter */}
        <div className="flex items-center border border-slate-200 rounded-md overflow-hidden">
          {[
            { key: "all", label: "All" },
            { key: "active", label: "Active" },
            { key: "inactive", label: "Inactive" },
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => setStatusFilter(opt.key)}
              className={`px-3 py-2 text-sm transition-colors ${
                statusFilter === opt.key
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* View toggle */}
        <div className="flex items-center border border-slate-200 rounded-md overflow-hidden">
          <button
            onClick={() => setViewMode("card")}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm transition-colors ${
              viewMode === "card"
                ? "bg-slate-900 text-white"
                : "bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700"
            }`}
            title="Card view"
          >
            <LayoutGrid className="w-4 h-4" />
            <span className="hidden sm:inline">Cards</span>
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm transition-colors ${
              viewMode === "list"
                ? "bg-slate-900 text-white"
                : "bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700"
            }`}
            title="List view"
          >
            <List className="w-4 h-4" />
            <span className="hidden sm:inline">List</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        viewMode === "card" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        )
      ) : campuses.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16">
            <svg width="96" height="80" viewBox="0 0 96 80" fill="none" className="mx-auto mb-4 opacity-80">
              <ellipse cx="48" cy="68" rx="30" ry="6" fill="#EEF2FF"/>
              <path d="M48 10C35 10 24 21 24 34c0 18 24 36 24 36s24-18 24-36C72 21 61 10 48 10z" fill="#EEF2FF" stroke="#C7D2FE" strokeWidth="1.5"/>
              <circle cx="48" cy="34" r="9" fill="#818CF8"/>
              <circle cx="48" cy="34" r="5" fill="white"/>
              <circle cx="78" cy="18" r="14" fill="#4F46E5"/>
              <path d="M74 18h8M78 14v8" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
            <p className="text-slate-500 font-medium">No campuses yet</p>
            <p className="text-sm text-slate-400 mt-1">Create your first campus to get started.</p>
            <Button className="mt-4" onClick={openCreate}>
              <Plus className="w-4 h-4 mr-2" /> Add Campus
            </Button>
          </CardContent>
        </Card>
      ) : filteredCampuses.length === 0 ? (
        /* No search results */
        <Card>
          <CardContent className="text-center py-12">
            <Search className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No campuses match your search</p>
            <p className="text-sm text-slate-400 mt-1">
              Try a different name, slug or address.
            </p>
            <Button variant="outline" className="mt-4" onClick={() => setSearchQuery("")}>
              Clear search
            </Button>
          </CardContent>
        </Card>
      ) : viewMode === "card" ? (
        /* ── Card Grid ── */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCampuses.map(campus => (
            <Card key={campus.id} className={!campus.is_active ? "opacity-60" : ""}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-indigo-500 shrink-0" />
                    <CardTitle className="text-base font-semibold">{campus.name}</CardTitle>
                  </div>
                  <Badge variant={campus.is_active ? "default" : "secondary"} className="text-xs">
                    {campus.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <p className="text-xs text-slate-400 font-mono mt-1">{campus.slug}</p>
              </CardHeader>
              <CardContent className="pt-0">
                {campus.address && (
                  <p className="text-sm text-slate-600 mb-3">{campus.address}</p>
                )}
                <div className="mb-4">
                  <AdminPopoverButton campus={campus} adminPopoverCampus={adminPopoverCampus} setAdminPopoverCampus={setAdminPopoverCampus} campusAdmins={campusAdmins} adminsFetching={adminsFetching} />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(campus)}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  {campus.is_active ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => setDeleteConfirm(campus)}
                      title="Deactivate"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                        onClick={() => activateMutation.mutate(campus.id)}
                        disabled={activateMutation.isPending}
                        title="Activate"
                      >
                        <RotateCcw className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => setHardDeleteConfirm(campus)}
                        title="Delete permanently"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* ── List View ── */
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[2fr_1.5fr_2fr_auto_auto_auto] items-center gap-4 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wide">
            <span>Name</span>
            <span>Slug</span>
            <span>Address</span>
            <span>Admins</span>
            <span>Status</span>
            <span className="text-right">Actions</span>
          </div>

          {/* Table rows */}
          <div className="divide-y divide-slate-100">
            {filteredCampuses.map(campus => (
              <div
                key={campus.id}
                className={`grid grid-cols-[2fr_1.5fr_2fr_auto_auto_auto] items-center gap-4 px-4 py-3 hover:bg-slate-50 transition-colors ${
                  !campus.is_active ? "opacity-60" : ""
                }`}
              >
                {/* Name */}
                <div className="flex items-center gap-2 min-w-0">
                  <MapPin className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                  <span className="text-sm font-medium text-slate-800 truncate">{campus.name}</span>
                </div>

                {/* Slug */}
                <span className="text-xs font-mono text-slate-400 truncate">{campus.slug}</span>

                {/* Address */}
                <span className="text-sm text-slate-500 truncate">
                  {campus.address || <span className="text-slate-300 italic">—</span>}
                </span>

                {/* Admin count */}
                <AdminPopoverButton campus={campus} adminPopoverCampus={adminPopoverCampus} setAdminPopoverCampus={setAdminPopoverCampus} campusAdmins={campusAdmins} adminsFetching={adminsFetching} />

                {/* Status badge */}
                <Badge variant={campus.is_active ? "default" : "secondary"} className="text-xs whitespace-nowrap">
                  {campus.is_active ? "Active" : "Inactive"}
                </Badge>

                {/* Actions */}
                <div className="flex items-center justify-end gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-slate-500 hover:text-slate-700"
                    onClick={() => openEdit(campus)}
                    title="Edit"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  {campus.is_active ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                      onClick={() => setDeleteConfirm(campus)}
                      title="Deactivate"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50"
                        onClick={() => activateMutation.mutate(campus.id)}
                        disabled={activateMutation.isPending}
                        title="Activate"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                        onClick={() => setHardDeleteConfirm(campus)}
                        title="Delete permanently"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Footer count */}
          <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-400">
            {filteredCampuses.length} campus{filteredCampuses.length !== 1 ? "es" : ""}
            {searchQuery.trim() && ` matching "${searchQuery}"`}
          </div>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={closeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCampus ? "Edit Campus" : "Add Campus"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="campus-name">Name *</Label>
              <Input
                id="campus-name"
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g. Main Campus"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="campus-slug">Slug *</Label>
              <Input
                id="campus-slug"
                value={form.slug}
                onChange={(e) => setForm(prev => ({ ...prev, slug: e.target.value }))}
                placeholder="e.g. main-campus"
              />
              <p className="text-xs text-slate-400">Unique identifier within the organization. Lowercase, hyphens only.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="campus-address">Address</Label>
              <Input
                id="campus-address"
                value={form.address}
                onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))}
                placeholder="e.g. 123 Main St, City"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {editingCampus ? "Save Changes" : "Create Campus"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate Campus</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Are you sure you want to deactivate <strong>{deleteConfirm?.name}</strong>?
            Admins assigned to this campus will still exist but the campus will be marked inactive.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => deactivateMutation.mutate(deleteConfirm.id)}
              disabled={deactivateMutation.isPending}
            >
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hard Delete Confirm Dialog */}
      <Dialog open={!!hardDeleteConfirm} onOpenChange={() => setHardDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Permanently delete campus?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-slate-600">
            <p>
              This will permanently remove <strong>{hardDeleteConfirm?.name}</strong> from the database.
              This action cannot be undone.
            </p>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              The server will refuse this if any admins, quizzes, or submissions still reference this campus.
              Move or remove those records first.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHardDeleteConfirm(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => hardDeleteMutation.mutate(hardDeleteConfirm.id)}
              disabled={hardDeleteMutation.isPending}
            >
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminPopoverButton({ campus, adminPopoverCampus, setAdminPopoverCampus, campusAdmins, adminsFetching }) {
  const isOpen = adminPopoverCampus?.id === campus.id;
  const count = campus.admin_count ?? 0;

  return (
    <Popover open={isOpen} onOpenChange={(open) => setAdminPopoverCampus(open ? campus : null)}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-indigo-600 transition-colors group">
          <Users className="w-3.5 h-3.5 group-hover:text-indigo-500" />
          <span className="underline-offset-2 group-hover:underline">
            {count} admin{count !== 1 ? "s" : ""}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="px-3 py-2.5 border-b border-slate-100">
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Admins — {campus.name}</span>
        </div>
        <div className="max-h-56 overflow-y-auto">
          {adminsFetching ? (
            <div className="flex items-center justify-center py-6 gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-xs">Loading…</span>
            </div>
          ) : campusAdmins.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">No admins assigned.</p>
          ) : (
            <ul className="divide-y divide-slate-50">
              {campusAdmins.map(admin => (
                <li key={admin.id} className="flex items-start gap-2.5 px-3 py-2.5">
                  <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5">
                    {(admin.name || admin.email || "?")[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{admin.name || "—"}</p>
                    <p className="text-xs text-slate-400 truncate flex items-center gap-1">
                      <Mail className="w-3 h-3 shrink-0" />{admin.email}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
